import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useROS } from "./ROS";
import { ConnectionState } from "./ROSContext";
import { CssBaseline, Button, Box, TextField } from "@mui/material";
import { ColorModeContext, useMode } from "../theme";
import MapScreen from "./MapScreen";
import { buildWsUrl } from "../utils/wsUrl";

// How long the app waits for the robot to answer /safety_reset (see callSafetyReset).
export const SAFETY_RESET_TIMEOUT_MS = 10000;


// Flat [lat0, lng0, lat1, lng1, ...] (the aggregator's path topics) -> [{lat, lng}, ...]
// for Google Maps. A pair with a non-finite number is dropped rather than
// handed to the map; a trailing odd value is ignored.
export const flatToLatLng = (flat) => {
    const result = [];
    for (let i = 0; i < (flat?.length || 0) - 1; i += 2) {
        const lat = flat[i];
        const lng = flat[i + 1];
        // typeof check first: Number(null) would be a "valid" 0.
        if (typeof lat === 'number' && typeof lng === 'number'
            && Number.isFinite(lat) && Number.isFinite(lng)) result.push({ lat, lng });
    }
    return result;
};

// /safety_state (otto_msgs/SafetyState) before any message has arrived.
const INITIAL_SAFETY_STATE = {
    safety_mode: "",
    active_actions: [],
    error_components: [],
    warning_components: [],
    highest_priority: 0,
    total_components: 0,
    state_changed: false,
    time_in_current_state: 0,
    autonomous_mode: false,
};

const EchoTopic = () => {
    // eslint-disable-next-line
    const [theme, colorMode] = useMode();

    const {
        createListener,
        createService,
        createSrv,
        isConnected,
        connectionState,
        url,
        changeUrl,
        toggleConnection,
    } = useROS();

    // All pre-computed data from the aggregator node
    const [appData, setAppData] = useState({
        latitude: 0,
        longitude: 0,
        heading_deg: 0,
        speed_mph: 0,
        gps_status: "No Data",
        cte_inches: 0,
        he_degrees: 0,
        nav_running: false,
        cmd_linear_x: 0,
        cmd_angular_z: 0,
        toolbar_status: "NOT_INIT",
        vacuum_pressure: 0,
        motor_temps: [0, 0, 0, 0],
        engine_rpm: 0,
        robot_armed: false,
        safety_mode: "",
        safety_active_actions: [],
        safety_error_components: [],
        safety_warning_components: [],
        safety_highest_priority: 0,
    });

    // Safety state from /safety_state (otto_msgs/SafetyState)
    const [safetyState, setSafetyState] = useState(INITIAL_SAFETY_STATE);

    // Fixposition fusion state (/fixposition/fpa/odomstatus). Subscribed
    // DIRECTLY rather than via the aggregator because the aggregator's
    // `gps_status` is built from the two GNSS receivers alone — it reported
    // "RTK Fixed" while the fusion engine was uninitialised and the robot was
    // publishing lat/lng 0,0. init_status is the only field that says whether
    // the fused position the app plots is actually usable.
    // null = never received (older robot build, or the driver is down).
    const [fusionStatus, setFusionStatus] = useState(null);

    // E-stop latch (/safety_manager/estop_latched, std_msgs/Bool). The robot
    // refuses START while it is true, yet /safety_state can already read NORMAL
    // because the fault itself has cleared — this is the only signal that a
    // /safety_reset is still needed. null = no message (older robot build that
    // does not publish it, or not connected): never treated as "clear".
    const [estopLatched, setEstopLatched] = useState(null);

    // Heartbeat counter — increments on each new appData message
    const [dataSeq, setDataSeq] = useState(0);

    // Paths from latched topics (flat arrays: [lat0, lng0, lat1, lng1, ...])
    const [globalPathLatLng, setGlobalPathLatLng] = useState([]);
    const [currentPathLatLng, setCurrentPathLatLng] = useState([]);
    // Active path = the path "now": the current + next segment the robot is
    // tracking (mission run, go_to_start, RTH, resume), from
    // /active_combined_path_rviz via the aggregator. Not the whole remaining
    // mission. It replaces the /curr_path line. activePathSeen = the robot publishes it
    // (latched, so it arrives right after subscribing, empty between runs);
    // a robot build without it keeps the old /curr_path line.
    const [activePathLatLng, setActivePathLatLng] = useState([]);
    const [activePathSeen, setActivePathSeen] = useState(false);

    // Stale data tracking
    const lastMsgTime = useRef({});
    const [dataStale, setDataStale] = useState({});
    const STALE_THRESHOLDS = useMemo(() => ({
        appData: 3000,
        globalPath: 30000,
        currentPath: 30000,
        // /safety_state comes with every /diagnostics_agg (~1 Hz). Its clock
        // also starts at connect (see the listener effect), so a robot whose
        // safety_manager never publishes reads "unknown", never "normal".
        safetyState: 5000,
        // odomstatus publishes fast; if it goes quiet the fusion state we are
        // gating the GPS status on is no longer trustworthy.
        fusion: 5000,
        // Published with every /safety_state, i.e. at the diagnostics
        // aggregator rate (~1 Hz). Quiet for 5 s = latch status unknown.
        estopLatched: 5000,
    }), []);

    // Check staleness every second
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            const stale = {};
            for (const [key, threshold] of Object.entries(STALE_THRESHOLDS)) {
                const lastTime = lastMsgTime.current[key];
                if (lastTime && (now - lastTime) > threshold) {
                    stale[key] = true;
                } else {
                    stale[key] = false;
                }
            }
            setDataStale(prev => {
                const changed = Object.keys(stale).some(k => prev[k] !== stale[k]);
                return changed ? stale : prev;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [STALE_THRESHOLDS]);

    const setterRef = useRef({});
    const hasInitializedUrl = useRef(false);
    // Default to the v5 robot. In production the app reaches robots through the
    // mobile.salin247.com nginx WebSocket proxy, which routes on the SHORT host
    // name (/ws/v5/) — so dropdown values must stay short names, not FQDNs.
    const [selectedIp, setSelectedIp] = useState(buildWsUrl("salinv5"));
    const defaultURL = buildWsUrl("salinv5");
    const ipHosts = [
        "v2",
        "v3",
        "salinv5",
        "rudinx",
        "gpu-pc",
        "localhost",
        "0.0.0.0",
        "host.docker.internal",
        "rosbridge",
    ];
    const ipOptions = ipHosts.map(buildWsUrl);

    useEffect(() => {
        setterRef.current = {
            setAppData,
            setGlobalPathLatLng,
            setCurrentPathLatLng,
            setActivePathLatLng,
            setActivePathSeen,
            setSafetyState,
            setFusionStatus,
            setEstopLatched,
        };
    }, []);

    // only runs once when ToggleConnect is first rendered (mounted)
    useEffect(() => {
        if (hasInitializedUrl.current) return;
        hasInitializedUrl.current = true;

        if (url !== defaultURL) {
            changeUrl(defaultURL);
        }
        setSelectedIp(defaultURL);

        // NOTE: do not call toggleAutoconnect() here — autoconnect defaults to
        // true in ROSContext, and toggling it on mount flips it OFF, which
        // disables every auto-reconnect path. Leave it alone.
    }, [changeUrl, url]);

    useEffect(() => {
        if (url && ipOptions.includes(url)) {
            setSelectedIp(url);
        }
    }, [url]);



        const COMPRESSION = "none";

        const listeners = useMemo(() => [
            // Single aggregated topic — all frequent data at 2 Hz from robot
            { topic: "/mobile_app/data", messageType: "otto_msgs/MobileAppData", handler: (msg) => {
              lastMsgTime.current.appData = Date.now();
              setterRef.current.setAppData(msg);
              setDataSeq(prev => prev + 1);
            }, throttleRate: 500 },

            // Latched path topics — re-received on every reconnect
            { topic: "/mobile_app/global_path_latlng", messageType: "std_msgs/Float64MultiArray", handler: (msg) => {
              lastMsgTime.current.globalPath = Date.now();
              setterRef.current.setGlobalPathLatLng(msg?.data || []);
            }, throttleRate: 0 },
            { topic: "/mobile_app/current_path_latlng", messageType: "std_msgs/Float64MultiArray", handler: (msg) => {
              lastMsgTime.current.currentPath = Date.now();
              setterRef.current.setCurrentPathLatLng(msg?.data || []);
            }, throttleRate: 0 },
            // Latched, on change (<= 1 Hz); an empty array = no active path.
            { topic: "/mobile_app/active_path_latlng", messageType: "std_msgs/Float64MultiArray", handler: (msg) => {
              setterRef.current.setActivePathLatLng(msg?.data || []);
              setterRef.current.setActivePathSeen(true);
            }, throttleRate: 0 },

            // Fixposition fusion status — direct subscription. Gates the GPS
            // status so two RTK-fixed antennas can no longer be reported as a
            // good fix while fusion is uninitialised.
            { topic: "/fixposition/fpa/odomstatus", messageType: "fixposition_driver_ros1/odomstatus", handler: (msg) => {
              lastMsgTime.current.fusion = Date.now();
              setterRef.current.setFusionStatus(msg);
            }, throttleRate: 500 },

            // E-stop latch — direct subscription (see estopLatched above)
            { topic: "/safety_manager/estop_latched", messageType: "std_msgs/Bool", handler: (msg) => {
              lastMsgTime.current.estopLatched = Date.now();
              setterRef.current.setEstopLatched(msg?.data === true);
            }, throttleRate: 500 },

            // Safety state — direct subscription
            { topic: "/safety_state", messageType: "otto_msgs/SafetyState", handler: (msg) => {
              lastMsgTime.current.safetyState = Date.now();
              setterRef.current.setSafetyState({
                safety_mode: msg?.safety_mode || "",
                active_actions: msg?.active_actions || [],
                error_components: msg?.error_components || [],
                warning_components: msg?.warning_components || [],
                highest_priority: msg?.highest_priority ?? 0,
                total_components: msg?.total_components ?? 0,
                state_changed: !!msg?.state_changed,
                time_in_current_state: msg?.time_in_current_state ?? 0,
                autonomous_mode: !!msg?.autonomous_mode,
              });
            }, throttleRate: 500 },
        ], []);

        useEffect(() => {
            if (!isConnected) return;
            // Fresh connection, possibly to a different robot: drop the old
            // safety state and start its staleness clock now.
            setSafetyState(INITIAL_SAFETY_STATE);
            lastMsgTime.current.safetyState = Date.now();
            // Same for the active path: the new robot re-sends it (latched) if
            // it has one; an older build never does and keeps its /curr_path line.
            setActivePathLatLng([]);
            setActivePathSeen(false);
            let isMounted = true;
          const createdListeners = listeners.map(({ topic, messageType, handler, throttleRate = 100, subscribeOnce = false }) => {
            const listener = createListener(topic, messageType, 1, COMPRESSION, throttleRate);
            if (isMounted) {
                if (subscribeOnce) {
                    const wrappedHandler = (msg) => {
                        handler(msg);
                        listener.unsubscribe();
                    };
                    listener.subscribe(wrappedHandler);
                } else {
                    listener.subscribe(handler);
                }
            }
            return listener;
          });

            return () => {
              if (!isConnected) return;
            isMounted = false;
            createdListeners.forEach(listener => {
              listener.unsubscribe();
            });
          };
        }, [isConnected]);


        // Forget the latch on disconnect, so a value from one robot can never
        // be shown while connected to another.
        useEffect(() => {
            if (isConnected) return;
            setEstopLatched(null);
            delete lastMsgTime.current.estopLatched;
        }, [isConnected]);

        const handleIp = (newIp) => {
            setSelectedIp(newIp);
            changeUrl(newIp);
        };

    // Service call: start/stop navigation
    const callSetStartNav = useCallback((startNav) => {
        return new Promise((resolve, reject) => {
            if (!isConnected) {
                reject(new Error("Not connected"));
                return;
            }
            const service = createService("/mission_tracker/set_start_nav", "otto_msgs/SetStartNav");
            const request = createSrv({ start_nav: startNav });
            service.callService(request, (result) => {
                resolve(result);
            }, (error) => {
                reject(error);
            });
        });
    }, [isConnected, createService, createSrv]);

    // Service call: manually raise/lower the planter link
    // target_position: 1 = RAISE (LIFTED), 2 = LOWER (LOWERED)
    const callManualSetLowerLink = useCallback((targetPosition, timeoutSec = 5.0) => {
        return new Promise((resolve, reject) => {
            if (!isConnected) {
                reject(new Error("Not connected"));
                return;
            }
            const service = createService("/planter/manual_set_lower_link", "otto_msgs/ManualSetLowerLink");
            const request = createSrv({ target_position: targetPosition, timeout_sec: timeoutSec });
            service.callService(request, (result) => {
                resolve(result);
            }, (error) => {
                reject(error);
            });
        });
    }, [isConnected, createService, createSrv]);

    // Service call: arm/disarm robot via PLC
    const callRobotEnable = useCallback((enabled) => {
        return new Promise((resolve, reject) => {
            if (!isConnected) {
                reject(new Error("Not connected"));
                return;
            }
            const service = createService("/plc/robot_enable", "std_srvs/SetBool");
            const request = createSrv({ data: enabled });
            service.callService(request, resolve, reject);
        });
    }, [isConnected, createService, createSrv]);

    // Service call: jump robot back to start of mission
    const callGoToStart = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!isConnected) {
                reject(new Error("Not connected"));
                return;
            }
            const service = createService("/mission_tracker/go_to_start", "otto_msgs/SetStartNav");
            const request = createSrv({ start_nav: true });
            service.callService(request, resolve, reject);
        });
    }, [isConnected, createService, createSrv]);

    // Service call: release a latched safety stop (safety_manager, std_srvs/Trigger).
    // An e-stop never clears by itself and the robot refuses START while one is
    // latched, so this is how the operator gets back to a startable robot. The
    // robot refuses while an emergency-level fault is still active and says why
    // in `message`. Unlike the calls above this one times out: a reply lost on a
    // dropped link would otherwise leave the operator watching "sending..." forever.
    const callSafetyReset = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!isConnected) {
                reject(new Error("Not connected"));
                return;
            }
            const timer = setTimeout(() => reject(new Error(
                `No reply from the robot within ${SAFETY_RESET_TIMEOUT_MS / 1000} s. ` +
                "The reset may or may not have been applied; check the safety state before starting."
            )), SAFETY_RESET_TIMEOUT_MS);
            const service = createService("/safety_reset", "std_srvs/Trigger");
            const request = createSrv({});
            service.callService(request,
                (result) => { clearTimeout(timer); resolve(result); },
                (error) => { clearTimeout(timer); reject(error); });
        });
    }, [isConnected, createService, createSrv]);

    // Convert flat [lat0, lng0, lat1, lng1, ...] arrays to [{lat, lng}, ...] for Google Maps
    const globalPathMap = useMemo(() => flatToLatLng(globalPathLatLng), [globalPathLatLng]);
    const currentPathMap = useMemo(() => flatToLatLng(currentPathLatLng), [currentPathLatLng]);
    const activePathMap = useMemo(() => flatToLatLng(activePathLatLng), [activePathLatLng]);


    return (
        <ColorModeContext.Provider value={colorMode}>
            {/* <ThemeProvider theme={theme}> */}
                <CssBaseline />
                <div className="app">
                    <div style={{ display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        backgroundColor: '#000',
                        overflow: 'hidden',
                    }}>

                    <main className="content" style={{ flex: 1, height: '100%' }}>
                        <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
                            <MapScreen
                                appData={appData}
                                fusionStatus={fusionStatus}
                                safetyState={safetyState}
                                estopLatched={estopLatched}
                                globalPathMap={globalPathMap}
                                currentPathMap={currentPathMap}
                                activePathMap={activePathMap}
                                activePathAvailable={activePathSeen}
                                dataStale={dataStale}
                                dataSeq={dataSeq}
                                callSetStartNav={callSetStartNav}
                                callManualSetLowerLink={callManualSetLowerLink}
                                callRobotEnable={callRobotEnable}
                                callGoToStart={callGoToStart}
                                callSafetyReset={callSafetyReset}
                            />
                            {/* Connection Control Panel */}
                            <Box
                                sx={{
                                    position: "absolute",
                                    bottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 8px))',
                                    right: 'max(8px, env(safe-area-inset-right, 8px))',
                                    zIndex: 1200,
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "center",
                                    backgroundColor: "rgba(255,255,255,0.95)",
                                    padding: "6px 8px",
                                    borderRadius: "8px",
                                }}
                            >
                                <TextField
                                    select
                                    size="small"
                                    label="IP"
                                    value={selectedIp}
                                    onChange={(event) => handleIp(event.target.value)}
                                    sx={{ width: '90px' }}
                                    SelectProps={{
                                        // Use the OS-native <select> picker. The MUI JS
                                        // popover menu mis-registers taps on mobile Safari
                                        // (the menu opens over the trigger near the bottom
                                        // safe-area, and the tap-through lands on the
                                        // neighbouring item → tapping v2 selected v3). A
                                        // native picker has no such off-by-one.
                                        native: true,
                                    }}
                                >
                                    {ipOptions.map((ip, i) => (
                                        <option key={ip} value={ip}>
                                            {ipHosts[i]}
                                        </option>
                                    ))}
                                </TextField>
                                <Button
                                    variant="contained"
                                    color={
                                        connectionState === ConnectionState.CONNECTED ? "success" :
                                        connectionState === ConnectionState.RECONNECTING ? "warning" :
                                        connectionState === ConnectionState.CONNECTING ? "info" :
                                        "error"
                                    }
                                    onClick={toggleConnection}
                                    sx={{
                                        width: '110px',
                                        minWidth: '110px',
                                        fontSize: '0.7rem',
                                        whiteSpace: 'nowrap',
                                        padding: '6px 2px',
                                    }}
                                >
                                    {connectionState === ConnectionState.CONNECTED ? "CONNECTED" :
                                     connectionState === ConnectionState.RECONNECTING ? "RECONNECTING" :
                                     connectionState === ConnectionState.CONNECTING ? "CONNECTING" :
                                     "CONNECT"}
                                </Button>
                            </Box>
                        </Box>
                    </main>
                </div>

                </div>
            {/* </ThemeProvider> */}
        </ColorModeContext.Provider>
  );
};


export default EchoTopic;
